import { useState, useRef, useCallback, KeyboardEvent, useMemo } from 'react';
import { ArrowUp, Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Content } from '@shared/types';
import { useConversation } from '@/services/conversationService';
import { useSendContentMutation, useIsLoading } from '@/services/messageService';
import { useTranslation } from 'react-i18next';

type ViewerFeedbackProps = {
  activeTab: '3d' | '2d';
};

export function ViewerFeedback({ activeTab }: ViewerFeedbackProps) {
  const { t, i18n } = useTranslation('common');
  const [text, setText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { conversation } = useConversation();
  const { mutate: sendMessage, isPending } = useSendContentMutation({ conversation });
  const isLoading = useIsLoading();

  const disabled = isLoading || isPending;

  const QUICK_SUGGESTIONS_3D = useMemo(
    () => t('viewer.feedback.improve3D', { returnObjects: true }) as string[],
    [i18n.language, t],
  );
  const QUICK_SUGGESTIONS_2D = useMemo(
    () => t('viewer.feedback.improve2D', { returnObjects: true }) as string[],
    [i18n.language, t],
  );

  const suggestions = activeTab === '3d' ? QUICK_SUGGESTIONS_3D : QUICK_SUGGESTIONS_2D;

  const handleSubmit = useCallback(
    (feedbackText: string) => {
      const trimmed = feedbackText.trim();
      if (!trimmed || disabled) return;

      const prefix = activeTab === '2d'
        ? t('viewer.feedback.improve2DPrefix')
        : t('viewer.feedback.improve3DPrefix');

      const content: Content = {
        text: `${prefix} ${trimmed}`,
        thinking: true,
      };

      sendMessage(content);
      setText('');
      setIsExpanded(false);
    },
    [activeTab, disabled, sendMessage, t],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(text);
      }
      if (e.key === 'Escape') {
        setIsExpanded(false);
        setText('');
      }
    },
    [handleSubmit, text],
  );

  return (
    <div className="absolute bottom-3 left-3 z-10 flex flex-col items-start gap-1.5">
      {/* Quick suggestions — shown when expanded */}
      {isExpanded && (
        <div className="flex flex-wrap gap-1 max-w-[340px]">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => handleSubmit(suggestion)}
              disabled={disabled}
              className={cn(
                'rounded-lg bg-white px-2.5 py-1.5 shadow-[0_0_4px_rgba(0,0,0,0.03)]',
                'border border-synergy-neutral-600',
                'text-[11px] font-medium text-synergy-text-secondary',
                'transition-all duration-150',
                '[@media(hover:hover)]:hover:bg-white [@media(hover:hover)]:hover:text-synergy-text-primary [@media(hover:hover)]:hover:shadow-[0_0_6px_rgba(0,0,0,0.05)]',
                'disabled:opacity-40 disabled:cursor-not-allowed',
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      {isExpanded ? (
        <div className="flex items-center overflow-hidden rounded-[10px] border border-synergy-neutral-700/60 bg-white shadow-[0_0_6px_rgba(0,0,0,0.04)]">
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              activeTab === '2d'
                ? t('viewer.feedback.placeholder2D')
                : t('viewer.feedback.placeholder3D')
            }
            disabled={disabled}
            autoFocus
            className="h-9 w-60 bg-transparent px-3 text-[12px] text-synergy-text-primary outline-none placeholder:text-synergy-neutral-400"
          />
          <button
            onClick={() => {
              setIsExpanded(false);
              setText('');
            }}
            className="flex h-9 w-8 items-center justify-center text-synergy-neutral-400 transition-colors [@media(hover:hover)]:hover:text-synergy-text-primary"
          >
            <X className="h-3 w-3" />
          </button>
          <button
            onClick={() => handleSubmit(text)}
            disabled={disabled || !text.trim()}
            className={cn(
              'mr-1.5 flex h-6 w-6 items-center justify-center rounded-md transition-all',
              text.trim()
                ? 'bg-synergy-neutral-50 text-white [@media(hover:hover)]:hover:bg-synergy-neutral-100'
                : 'bg-synergy-neutral-800 text-synergy-neutral-400',
              'disabled:opacity-40 disabled:cursor-not-allowed',
            )}
          >
            <ArrowUp className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => {
            setIsExpanded(true);
            setTimeout(() => inputRef.current?.focus(), 100);
          }}
          disabled={disabled}
          className={cn(
            'flex items-center gap-1.5 overflow-hidden rounded-[10px]',
            'border border-synergy-neutral-600 bg-white px-3 py-2',
            'shadow-[0_0_4px_rgba(0,0,0,0.03)]',
            'text-[11px] font-medium text-synergy-text-secondary',
            'transition-all duration-150',
            '[@media(hover:hover)]:hover:bg-white [@media(hover:hover)]:hover:text-synergy-text-primary [@media(hover:hover)]:hover:shadow-[0_0_6px_rgba(0,0,0,0.05)]',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          <Sparkles className="h-3 w-3" />
          {t('viewer.feedback.improveButton')}
        </button>
      )}
    </div>
  );
}
